import React from "react";
import { useAuth } from "../../context/AuthContext";
import { CASE_THUMBS, optimize } from "../../data/casesData";
import { Gift, ArrowRight, Users, Swords } from "lucide-react";

// The box shown in the primary banner — swap this id for any case.
const BANNER_BOX_ID = "cupertino";

export default function HeroBanners({ onRefer }) {
  const { user } = useAuth();

  const claim = () => {
    if (!user) window.dispatchEvent(new Event("uc:open-auth"));
    else window.dispatchEvent(new CustomEvent("uc:open-deposit"));
  };

  const badge = user ? "Now live" : "Free box available";
  const cta = user ? "Deposit now" : "Claim now";

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
      {/* Left / primary banner */}
      <div
        data-testid="hero-banner-claim-free"
        className="uc-fadeup group relative col-span-1 flex min-h-[240px] flex-col justify-between overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#1B1733] via-[#171330] to-[#120F22] p-6 shadow-lg shadow-black/30 md:p-8 lg:col-span-8"
      >
        {/* soft glow behind the box */}
        <div className="pointer-events-none absolute right-[6%] top-1/2 h-[280px] w-[280px] -translate-y-1/2 rounded-full bg-[var(--uc-primary)]/20 blur-3xl" />
        <img
          src={optimize(CASE_THUMBS[BANNER_BOX_ID], 620)}
          alt=""
          className="pointer-events-none absolute bottom-3 -right-2 h-[58%] w-auto rotate-[-15deg] select-none object-contain drop-shadow-[0_18px_36px_rgba(0,0,0,0.5)] transition-transform duration-500 group-hover:scale-[1.05] sm:bottom-auto sm:right-6 sm:top-1/2 sm:h-[80%] sm:-translate-y-1/2 sm:rotate-0 md:right-8"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#171330] via-[#171330]/75 to-transparent" />
        <div className="relative max-w-[62%] sm:max-w-[60%]">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--uc-green)]/15 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-[var(--uc-green)] ring-1 ring-inset ring-[var(--uc-green)]/30">
            {user ? <Swords className="h-3.5 w-3.5" /> : <Gift className="h-3.5 w-3.5" />} {badge}
          </span>
          {user ? (
            <>
              <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.05] text-white md:text-[40px]">
                Case <span className="text-[#C4B5FD]">Battles</span> are out now
              </h2>
              <p className="mt-2 max-w-md text-sm text-[var(--uc-muted)]">
                Go head-to-head in 1v1 &amp; 2v2 battles — highest total takes the whole pot. Top up and jump in.
              </p>
            </>
          ) : (
            <>
              <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.05] text-white md:text-[40px]">
                Sign up today &amp; claim your <span className="text-[#C4B5FD]">FREE</span> box
              </h2>
              <p className="mt-2 max-w-md text-sm text-[var(--uc-muted)]">
                Unbox luxury sneakers, watches, tech and rare collectibles instantly — provably fair, every spin.
              </p>
            </>
          )}
        </div>
        <button
          data-testid="hero-claim-btn"
          onClick={claim}
          className="relative z-20 mt-5 inline-flex w-fit items-center gap-2 rounded-2xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] px-6 py-3 text-sm font-bold text-white shadow-lg shadow-purple-900/40 transition-all hover:scale-[1.03] hover:from-[#7C3AED] hover:to-[#5B21B6]"
        >
          {cta} <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      {/* Right / referral banner */}
      <div
        data-testid="hero-banner-referral"
        className="uc-fadeup relative col-span-1 flex min-h-[230px] flex-col justify-between overflow-hidden rounded-3xl border border-purple-500/20 bg-gradient-to-br from-[#3B0764] via-[#1E1035] to-[#0F0C16] p-6 shadow-xl md:p-8 lg:col-span-4"
        style={{ animationDelay: "0.08s" }}
      >
        <div className="relative">
          <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-white">
            Earn 5% – 10%
          </span>
          <h3 className="mt-4 font-heading text-2xl font-extrabold leading-tight text-white">
            Earn up to 10% from your friends
          </h3>
          <p className="mt-2 text-sm text-[var(--uc-muted)]">
            Share your referral code and collect passive revenue on every deposit they make.
          </p>
        </div>
        <button
          data-testid="hero-refer-btn"
          onClick={onRefer}
          className="mt-5 inline-flex w-fit items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-bold text-white transition-all hover:scale-[1.03] hover:bg-white/10"
        >
          <Users className="h-4 w-4" /> Refer a friend
        </button>
      </div>
    </div>
  );
}
